/**
 * Переходы режима тренировки переключения раскладок.
 *
 * Режим собирает занятие сам, но mastery и XP считаются теми же функциями,
 * что и после обычной тренировки: результат для пользователя один и тот же, а
 * два разных пути его подсчёта разошлись бы (docs/ARCHITECTURE.md §5).
 */

import type { SessionId } from '@type2x/shared-contracts';
import type { TypingSession } from '@type2x/typing-engine';
import { DEFAULT_LEARNING_CONFIG } from '@type2x/learning-engine';
import type { Workspace } from '@type2x/feature-personal-workspace';
import {
  activeProfile,
  appendXp,
  recordSession,
  upsertMastery,
} from '@type2x/feature-personal-workspace';
import {
  applySwitchOutcomes,
  buildLayoutSwitchPlan,
  collectSwitchOutcomes,
  type LayoutSwitchPlan,
  type LayoutSwitchSnapshot,
} from '@type2x/feature-layout-switch';
import { buildSessionSummary } from '@type2x/feature-progress';
import { buildSessionPayload } from '@type2x/feature-sync';

import type { Runtime } from '../runtime.js';
import type { AppScreen } from './app-state.js';
import { countXpToday, resultScreen, type Transition } from './flow.js';

const CONFIG = DEFAULT_LEARNING_CONFIG;

/**
 * Снимок навыка для режима.
 *
 * Раскладка в снимок не входит: занятие идёт сразу в двух, а язык профиля
 * решает только то, с какой из них начать.
 */
export function layoutSwitchSnapshot(workspace: Workspace): LayoutSwitchSnapshot {
  const profile = activeProfile(workspace);
  return { language: profile.language, stats: profile.stats };
}

/** Экран режима со свежим занятием. */
export function layoutSwitchScreen(runtime: Runtime, workspace: Workspace): AppScreen {
  return {
    name: 'layout-switch',
    plan: buildLayoutSwitchPlan(
      layoutSwitchSnapshot(workspace),
      `${runtime.installationId}:layout-switch:${runtime.clock.epochMs()}`,
    ),
    sessionId: runtime.ids.newSessionId(),
  };
}

/** Завершает занятие: обновляет переходы, начисляет XP и собирает итог. */
export function finishLayoutSwitch(
  runtime: Runtime,
  workspace: Workspace,
  plan: LayoutSwitchPlan,
  session: TypingSession,
  sessionId: SessionId,
): Transition {
  const now = runtime.clock.isoNow();
  const profile = activeProfile(workspace);

  const outcomes = collectSwitchOutcomes(plan, session.keystrokeEvents, session.typingErrors);
  const statsAfter = applySwitchOutcomes(plan.units, profile.stats, outcomes, CONFIG, now);

  // Переход мог встретиться впервые: его единица создаётся вместе с итогом.
  let next = upsertMastery(workspace, plan.units, statsAfter, now);
  next = recordSession(next, session.metrics, now);

  const summary = buildSessionSummary({
    metrics: session.metrics,
    baseline: profile.baseline,
    units: activeProfile(next).units,
    statsBefore: profile.stats,
    statsAfter,
    previousXp: next.xpLedger.reduce((sum, item) => sum + item.amount, 0),
    xpOccurrencesToday: countXpToday(next, now),
    activeDays: activeProfile(next).practiceDays.length,
    retained: false,
    isControlled: false,
    awardedAt: now,
    config: CONFIG,
  });

  for (const transaction of summary.xpAwarded) {
    next = appendXp(next, transaction, now);
  }

  return {
    workspace: next,
    screen: resultScreen(next, summary, { kind: 'layout-switch' }, now),
    sessionPayload: buildSessionPayload({
      syncId: runtime.ids.newSyncId(),
      sessionId,
      mode: plan.mode,
      language: profile.language,
      // Раскладка итога — та, с которой занятие начиналось: одной здесь не
      // хватает по существу, а поле в контракте одно.
      layoutId: plan.layoutId,
      startedAt: new Date(runtime.clock.epochMs() - session.metrics.elapsedMs).toISOString(),
      durationMs: session.metrics.elapsedMs,
      metrics: session.metrics,
      outcomes,
      statsAfter,
      errors: session.typingErrors,
      isControlled: false,
    }),
  };
}
